// src/components/atomic-crm/tasks/TaskCard.tsx
import {
  useRecordContext,
  useTranslate,
  useLocale,
  useGetList,
  useDelete,
  useNotify,
  useUpdate,
} from "ra-core";
import { Card, CardContent } from "@/components/ds/ui/card";
import { Badge } from "@/components/ds/ui/badge";
import { Button } from "@/components/ds/ui/button";
import {
  Briefcase,
  CheckCircle2,
  Circle,
  CircleDot,
  PauseCircle,
  XCircle,
  ChevronDown,
  CalendarClock,
  Building2,
  User,
  MoreHorizontal,
  PlusCircle,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ds/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ds/ui/popover";
import { formatDistanceToNow, format } from "date-fns";
import { getDateFnsLocale } from "@/i18n/date-fns";
import { cn } from "@/lib/utils";
import { useState } from "react";
import { TaskEdit } from "./TaskEdit";

import { useQuery } from "@tanstack/react-query";
import { supabase } from "../providers/supabase/supabase";

type TaskStatus = "todo" | "in_progress" | "blocked" | "done" | "cancelled";

const STATUS_ORDER: TaskStatus[] = [
  "todo",
  "in_progress",
  "blocked",
  "done",
  "cancelled",
];

const STATUS_LABEL: Record<TaskStatus, string> = {
  todo: "Open",
  in_progress: "In progress",
  blocked: "Blocked",
  done: "Done",
  cancelled: "Cancelled",
};

// Tailwind class pairs per status: [pillClasses, iconColor]
const STATUS_STYLE: Record<TaskStatus, string> = {
  todo: "border-muted-foreground/30 text-muted-foreground hover:bg-muted hover:text-foreground",
  in_progress:
    "border-blue-500/40 bg-blue-500/10 text-blue-500 hover:bg-blue-500/20",
  blocked:
    "border-amber-500/40 bg-amber-500/10 text-amber-500 hover:bg-amber-500/20",
  done: "border-emerald-500/40 bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500/20",
  cancelled: "border-red-500/40 bg-red-500/10 text-red-500 hover:bg-red-500/20",
};

const statusIcon = (status: TaskStatus, className = "h-3 w-3") => {
  switch (status) {
    case "todo":
      return <Circle className={className} />;
    case "in_progress":
      return <CircleDot className={className} />;
    case "blocked":
      return <PauseCircle className={className} />;
    case "done":
      return <CheckCircle2 className={className} />;
    case "cancelled":
      return <XCircle className={className} />;
  }
};

const CLOSED = new Set<TaskStatus>(["done", "cancelled"]);

export const TaskCard = () => {
  const task = useRecordContext<any>();
  const [editOpen, setEditOpen] = useState(false);

  const translate = useTranslate();
  const locale = useLocale();

  const { data: salesRaw } = useGetList("sales", {
    pagination: { page: 1, perPage: 200 },
  });
  const sales = Array.isArray(salesRaw) ? salesRaw : salesRaw?.data || [];

  if (!task) return null;

  const creator = sales.find((s: any) => s.id === task.sales_id);

  const { data: assigneesRaw } = useQuery({
    queryKey: ["crm_activity_assignees", task.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("crm_activity_assignees")
        .select("sales_id, assigned_at")
        .eq("activity_id", task.id)
        .order("assigned_at", { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  });

  const assigneeIds = (assigneesRaw ?? []).map((r: any) => Number(r.sales_id));
  const assignees = sales.filter((s: any) =>
    assigneeIds.includes(Number(s.id)),
  );

  // Derive current status; fall back to done_date for legacy rows.
  const status: TaskStatus =
    (task.status as TaskStatus) ?? (task.done_date ? "done" : "todo");
  const isClosed = CLOSED.has(status);
  const isDone = status === "done";
  const isCancelled = status === "cancelled";
  const isOverdue =
    !isClosed && task.due_date && new Date(task.due_date) < new Date();

  const d = task.due_date ? new Date(task.due_date) : null;
  const hasTime = d ? d.getHours() !== 0 || d.getMinutes() !== 0 : false;
  const dateStr = d
    ? hasTime
      ? format(d, "PPp", { locale: getDateFnsLocale(locale) })
      : format(d, "PP", { locale: getDateFnsLocale(locale) })
    : "";

  const handleEdit = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setEditOpen(true);
  };

  const [deleteOne] = useDelete();
  const notify = useNotify();

  const handleDelete = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!confirm("Delete this activity?")) return;
    deleteOne(
      "crm_activities",
      { id: task.id, previousData: task },
      {
        onSuccess: () =>
          notify("Activity deleted", { type: "info", undoable: true }),
      },
    );
  };

  const [update] = useUpdate();
  const [optimisticStatus, setOptimisticStatus] = useState<TaskStatus | null>(
    null,
  );
  const effectiveStatus = optimisticStatus ?? status;

  /**
   * Set the task's status. Handles done_date transitions:
   * - Moving into done/cancelled → set done_date if not set
   * - Moving into an open status → clear done_date
   * - Switching between done and cancelled → keep done_date
   */
  const handleStatusChange = (nextStatus: TaskStatus) => {
    if (nextStatus === effectiveStatus) return;

    setOptimisticStatus(nextStatus);

    const nextIsClosed = CLOSED.has(nextStatus);
    const doneDate = nextIsClosed
      ? (task.done_date ?? new Date().toISOString())
      : null;

    setTimeout(() => {
      update(
        "crm_activities",
        {
          id: task.id,
          data: { status: nextStatus, done_date: doneDate },
          previousData: task,
        },
        {
          onSuccess: () => {
            notify(`Status: ${STATUS_LABEL[nextStatus]}`, {
              type: "info",
              undoable: true,
              autoHideDuration: 10000,
            });
            setOptimisticStatus(null);
          },
          onError: (err: any) => {
            setOptimisticStatus(null);
            notify(`Failed: ${err.message}`, { type: "error" });
          },
        },
      );
    }, 300);
  };

  /**
   * Pill direct-click behavior:
   * - Any open status → done
   * - Any closed status → todo (reopen)
   */
  const handlePillClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const next: TaskStatus = CLOSED.has(effectiveStatus) ? "todo" : "done";
    handleStatusChange(next);
  };

  const pillStyle = STATUS_STYLE[effectiveStatus];

  return (
    <>
      <Card
        className={cn(
          "cursor-pointer transition-colors hover:bg-muted/30",
          isClosed && "opacity-70",
          isOverdue && "border-l-4 border-l-destructive",
          isCancelled && "opacity-60",
        )}
        onClick={handleEdit}
      >
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            {/* Status pill + arrow (Popover for full menu) */}
            <div
              className="mt-0.5 shrink-0 flex items-center"
              onClick={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={handlePillClick}
                className={cn(
                  "inline-flex items-center gap-1 rounded-l-full border border-r-0 px-2.5 py-0.5 text-[11px] font-medium transition-colors",
                  pillStyle,
                )}
                aria-label={`Current status: ${STATUS_LABEL[effectiveStatus]}. Click to toggle.`}
              >
                {statusIcon(effectiveStatus)}
                {STATUS_LABEL[effectiveStatus]}
              </button>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className={cn(
                      "inline-flex items-center rounded-r-full border px-1 py-0.5 text-[11px] transition-colors",
                      pillStyle,
                    )}
                    aria-label="Change status"
                  >
                    <ChevronDown className="h-3 w-3" />
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-44 p-1">
                  <div className="flex flex-col">
                    {STATUS_ORDER.map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => handleStatusChange(s)}
                        className={cn(
                          "flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground",
                          s === effectiveStatus && "bg-muted/60 font-medium",
                        )}
                      >
                        {statusIcon(s, "h-3.5 w-3.5")}
                        {STATUS_LABEL[s]}
                      </button>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            {/* Body */}
            <div className="flex-1 min-w-0 space-y-2">
              <p
                className={cn(
                  "text-sm font-medium leading-snug line-clamp-2",
                  (isDone || isCancelled) &&
                    "line-through text-muted-foreground",
                )}
              >
                {task.text ||
                  translate("crm.task.untitled", { _: "Untitled task" })}
              </p>

              {/* Meta row 1 */}
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                {task.due_date && (
                  <span
                    className={cn(
                      "flex items-center gap-1",
                      isOverdue && "text-destructive font-medium",
                    )}
                  >
                    <CalendarClock className="h-3 w-3" />
                    {isDone && task.done_date
                      ? `Done ${formatDistanceToNow(new Date(task.done_date), {
                          addSuffix: true,
                          locale: getDateFnsLocale(locale),
                        })}`
                      : isCancelled && task.done_date
                        ? `Cancelled ${formatDistanceToNow(
                            new Date(task.done_date),
                            {
                              addSuffix: true,
                              locale: getDateFnsLocale(locale),
                            },
                          )}`
                        : isOverdue
                          ? `Overdue · ${dateStr}`
                          : dateStr}
                  </span>
                )}

                {task.priority && (
                  <Badge
                    variant={
                      task.priority?.toLowerCase() === "high"
                        ? "destructive"
                        : task.priority?.toLowerCase() === "medium"
                          ? "default"
                          : "secondary"
                    }
                    className="text-[10px] h-5"
                  >
                    {task.priority}
                  </Badge>
                )}

                {task.company_name && (
                  <span className="flex items-center gap-1">
                    <Building2 className="h-3 w-3" />
                    {task.company_name}
                  </span>
                )}

                {assignees.length > 0 && (
                  <span
                    className="flex items-center gap-1 min-w-0"
                    title={assignees
                      .map((s: any) => `${s.first_name} ${s.last_name}`)
                      .join(", ")}
                  >
                    <User className="h-3 w-3 shrink-0" />
                    <span className="truncate">
                      {assignees.length <= 3
                        ? assignees.map((s: any) => s.first_name).join(", ")
                        : `${assignees
                            .slice(0, 3)
                            .map((s: any) => s.first_name)
                            .join(", ")} +${assignees.length - 3}`}
                    </span>
                  </span>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground/80">
                {task.contact_name && (
                  <span className="flex items-center gap-1">
                    <User className="h-3 w-3" />
                    {task.contact_name}
                  </span>
                )}

                {task.deal_name && (
                  <span className="flex items-center gap-1">
                    <Briefcase className="h-3 w-3" />
                    {task.deal_name}
                  </span>
                )}
              </div>

              {/* Meta row 2 */}
              <div className="flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground/80">
                {creator ? (
                  <span className="flex items-center gap-1">
                    <PlusCircle className="h-3 w-3" />
                    {translate("crm.task.created_by", { _: "Added by" })}{" "}
                    <span className="font-medium">
                      {creator.first_name} {creator.last_name}
                    </span>
                  </span>
                ) : null}
                {task.created_at && (
                  <>
                    <span>·</span>
                    <span
                      title={format(new Date(task.created_at), "PPpp", {
                        locale: getDateFnsLocale(locale),
                      })}
                    >
                      {formatDistanceToNow(new Date(task.created_at), {
                        addSuffix: true,
                        locale: getDateFnsLocale(locale),
                      })}
                    </span>
                  </>
                )}
              </div>
            </div>

            {/* Row menu */}
            <div
              onClick={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              className="shrink-0"
            >
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={handleEdit}>Edit</DropdownMenuItem>
                  <DropdownMenuItem
                    className="text-destructive focus:text-destructive"
                    onClick={handleDelete}
                  >
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </CardContent>
      </Card>
      {editOpen && (
        <TaskEdit
          open={editOpen}
          close={() => setEditOpen(false)}
          taskId={task.id}
        />
      )}
    </>
  );
};
